import { useNavigate, useParams } from "react-router-dom";
import { useTreeDetail } from "@/hooks";
import QrCode, { fichaUrl } from "@/components/shared/QrCode";

/**
 * Etiqueta do exemplar para fixar em campo: codigo CTA, especie e QR Code que
 * abre a ficha.  Impressa em A4, com contorno para recortar (90 x 55 mm).
 */
export default function TreeLabelPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const treeId = id ? Number(id) : null;
  const { data: tree, loading, error } = useTreeDetail(treeId);

  if (loading && !tree) {
    return <p className="p-8 text-center text-sm text-gray-500">Preparando etiqueta…</p>;
  }
  if (error || !tree || !treeId) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-gray-700">{error ?? "Exemplar não encontrado."}</p>
        <button onClick={() => navigate("/cta")} className="btn-outline text-sm mt-3">Voltar ao CTA</button>
      </div>
    );
  }

  const code = tree.cta_code ?? `#${tree.id}`;
  const popular = tree.species_popular_name ?? "Espécie não identificada";
  const scientific = tree.species_scientific_name;
  const address = [tree.street, tree.neighborhood].filter(Boolean).join(" · ");

  return (
    <div className="bg-gray-100 min-h-screen print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex flex-wrap items-center gap-3">
        <button onClick={() => window.print()} className="btn-primary text-sm">
          <i className="ri-printer-line" aria-hidden="true"></i>
          Imprimir etiqueta
        </button>
        <span className="text-xs text-gray-500">Imprima em escala 100% (sem “ajustar à página”) para manter 90 × 55 mm.</span>
        <button onClick={() => navigate(`/cta/${treeId}`)} className="btn-outline text-sm ml-auto">
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          Voltar à ficha
        </button>
      </div>

      <div className="p-6 print:p-0 flex justify-center print:justify-start">
        <div
          className="bg-white border border-dashed border-gray-400 print:border-gray-500 flex items-stretch gap-[4mm] p-[4mm] text-gray-900"
          style={{ width: "90mm", height: "55mm", boxSizing: "border-box" }}
        >
          <div className="flex flex-col items-center justify-center shrink-0">
            <QrCode value={fichaUrl(treeId)} alt={`QR Code da ficha ${code}`} className="w-[34mm] h-[34mm]" />
            <p className="text-[6.5pt] text-gray-500 mt-[1mm]">Aponte a câmera</p>
          </div>
          <div className="flex flex-col justify-between min-w-0 flex-1">
            <div>
              <p className="text-[6.5pt] uppercase tracking-wider text-[#1B6E4F] font-semibold">FloraIQ · CTA Arbóreo</p>
              <p className="font-mono font-bold text-[13pt] leading-tight mt-[1mm] break-all">{code}</p>
              <p className="text-[9pt] font-semibold leading-tight mt-[1.5mm]">{popular}</p>
              {scientific && <p className="text-[7.5pt] italic text-gray-600 leading-tight">{scientific}</p>}
            </div>
            <div>
              {address && <p className="text-[6.5pt] text-gray-600 leading-tight">{address}</p>}
              <p className="text-[6pt] text-gray-400 leading-tight mt-[0.5mm] break-all">{fichaUrl(treeId)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
